import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { userRequest } from '../utils/requestMethods';
import { useStateContext } from './ContextProvider';

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const { school, schoolId, user } = useStateContext();

  const [subscription, setSubscription] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [graceDismissed, setGraceDismissed] = useState(false);
  const [endpointsUnavailable, setEndpointsUnavailable] = useState(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  const fetchSubscription = useCallback(async () => {
    if (!schoolId) return;
    try {
      const { data } = await userRequest.get(`/subscriptions/current/${schoolId}`);
      if (data && typeof data === 'object' && data._id) {
        setSubscription(data);
      } else {
        setSubscription(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setEndpointsUnavailable(true);
        setSubscription(null);
      } else {
        setError(err.response?.data?.message || 'Failed to fetch subscription');
      }
    }
  }, [schoolId]);

  const fetchAccess = useCallback(async () => {
    if (!schoolId) return;
    try {
      const { data } = await userRequest.get(`/subscriptions/access/${schoolId}`);
      // Normalize snake_case API response to camelCase
      setAccess({
        hasAccess: data.has_access ?? data.hasAccess,
        accessLevel: data.access_level ?? data.accessLevel,
        subscriptionStatus: data.subscription_status ?? data.status,
        daysOverdue: data.days_overdue ?? data.daysOverdue ?? 0,
        message: data.message || null,
      });
    } catch (err) {
      if (err.response?.status === 404) {
        setEndpointsUnavailable(true);
        setAccess(null);
      } else {
        setAccess({
          hasAccess: false,
          accessLevel: 'none',
          subscriptionStatus: 'restricted',
          daysOverdue: 0,
          message: 'Unable to verify subscription status. Please try again.',
        });
      }
    }
  }, [schoolId]);

  const refreshSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchSubscription(), fetchAccess()]);
    setHasFetchedOnce(true);
    setLoading(false);
  }, [fetchSubscription, fetchAccess]);

  useEffect(() => {
    if (schoolId) {
      refreshSubscription();
    } else {
      // schoolId hasn't resolved yet (e.g. stale/incomplete cached session data) —
      // this is not the same as "no subscription found", so don't mark a fetch as attempted.
      setLoading(false);
    }
  }, [schoolId, refreshSubscription]);

  // Use subscription.status for UI banners (actual payment status)
  // Use access endpoint for read/write permissions
  const subscriptionStatus = subscription?.status || null;
  const isWriteBlocked = ['restricted', 'suspended'].includes(subscriptionStatus);
  const isFullyBlocked = subscriptionStatus === 'suspended';
  const isGrace = subscriptionStatus === 'grace';
  // Only trust "no subscription" once we've actually completed a fetch for a resolved
  // schoolId — otherwise a stale/incomplete cached session (schoolId not yet resolved)
  // looks identical to "confirmed no subscription" and incorrectly blocks the school.
  const hasNoSubscription = !loading && !endpointsUnavailable && hasFetchedOnce && subscription === null && access === null;
  const canManageBilling = user?.role === 0 || user?.role === 2;

  const contextValue = useMemo(() => ({
    subscription,
    access,
    loading,
    error,
    subscriptionStatus,
    isWriteBlocked,
    isFullyBlocked,
    isGrace,
    hasNoSubscription,
    endpointsUnavailable,
    canManageBilling,
    graceDismissed,
    setGraceDismissed,
    refreshSubscription,
  }), [
    subscription, access, loading, error,
    subscriptionStatus, isWriteBlocked, isFullyBlocked, isGrace,
    hasNoSubscription, endpointsUnavailable, canManageBilling, graceDismissed, refreshSubscription,
  ]);

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
